import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import toast from 'react-hot-toast';

import { toastApiError } from '../../../utils/errorMessage';

import {
  suspiciousLoginApi,
  type SuspiciousLoginAttempt,
  type SuspiciousLoginSeverity,
  SuspiciousLoginResolutionStatus,
  STATUS_LABELS,
  STATUS_COLORS,
  SEVERITY_LABELS_TR,
  SEVERITY_COLORS,
} from 'src/api/suspiciousLogin.api';
import { Label } from 'src/components/label';
import { RouterLink } from 'src/routes/components';

interface SuspiciousLoginTabProps {
  employeeId: string;
}

export default function SuspiciousLoginTab({ employeeId }: SuspiciousLoginTabProps) {
  const [attempts, setAttempts] = useState<SuspiciousLoginAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState<SuspiciousLoginAttempt | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingResolution, setPendingResolution] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const loadAttempts = async () => {
    try {
      setLoading(true);
      const data = await suspiciousLoginApi.getEmployeeAttempts(employeeId);
      setAttempts(data);
    } catch (error: any) {
      toast.error('Şüpheli giriş denemeleri yüklenirken hata oluştu');
      console.error('Error loading suspicious login attempts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttempts();
  }, [employeeId]);

  const handleReview = (attempt: SuspiciousLoginAttempt) => {
    setSelectedAttempt(attempt);
    setNotes('');
    setPendingResolution(null);
    setDialogOpen(true);
  };

  const handleResolutionClick = (resolution: string) => {
    setPendingResolution(resolution);
    setConfirmDialogOpen(true);
  };

  const handleConfirmResolve = async () => {
    if (!selectedAttempt || !pendingResolution) return;

    try {
      setResolving(true);
      // FE-15: toast mesajını sunucunun gerçek sonucundan (resolutionStatus) türet,
      // yerel pendingResolution'dan değil → server-driven, tutarlı geri bildirim.
      const res = await suspiciousLoginApi.resolveAttempt(selectedAttempt.id, {
        resolution: pendingResolution,
        notes: notes.trim() || undefined,
      });
      toast.success(
        res.resolutionStatus === SuspiciousLoginResolutionStatus.APPROVED
          ? 'Cihaz sıfırlandı, çalışan tekrar giriş yapabilir'
          : 'Çalışan hesabı engellendi'
      );
      setConfirmDialogOpen(false);
      setDialogOpen(false);
      loadAttempts();
    } catch (error: any) {
      toastApiError(error, 'İşlem sırasında hata oluştu');
    } finally {
      // FE-15: stale state sızıntısını önle — sonraki açılış temiz başlasın.
      setResolving(false);
      setPendingResolution(null);
      setSelectedAttempt(null);
      setNotes('');
    }
  };

  const getStatusIcon = (status: SuspiciousLoginResolutionStatus) => {
    switch (status) {
      case SuspiciousLoginResolutionStatus.APPROVED:
        return <CheckCircleIcon fontSize="small" />;
      case SuspiciousLoginResolutionStatus.REJECTED:
        return <CancelIcon fontSize="small" />;
      default:
        return <WarningIcon fontSize="small" />;
    }
  };

  const truncateId = (id: string) => {
    if (!id) return '-';
    return id.length > 16 ? `${id.substring(0, 16)}...` : id;
  };

  // Eski satırlarda severity eksik gelebilir — migration default'u ile tutarlı olarak WARNING varsay
  const getSeverity = (attempt: SuspiciousLoginAttempt): SuspiciousLoginSeverity =>
    attempt.severity ?? 'WARNING';

  const renderCounterpartyLink = (attempt: SuspiciousLoginAttempt) =>
    attempt.counterpartyEmployeeId ? (
      <Link
        component={RouterLink}
        href={`/dashboard/employee/${attempt.counterpartyEmployeeId}`}
        variant="body2"
        underline="hover"
      >
        {attempt.counterpartyEmployeeName || attempt.counterpartyEmployeeId.slice(0, 8)}
      </Link>
    ) : (
      '—'
    );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Şüpheli Giriş Denemeleri</Typography>
        <Button variant="outlined" onClick={loadAttempts}>
          Yenile
        </Button>
      </Stack>

      {attempts.length === 0 ? (
        <Alert severity="info">Bu çalışan için şüpheli giriş denemesi bulunmuyor.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Denenen Cihaz</TableCell>
                <TableCell>Mevcut Cihaz</TableCell>
                <TableCell>Cihaz Modeli</TableCell>
                <TableCell>Önem</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Çakışan Çalışan</TableCell>
                <TableCell>İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attempts.map((attempt) => (
                <TableRow key={attempt.id}>
                  <TableCell>
                    {format(new Date(attempt.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={attempt.attemptedDeviceId}
                    >
                      {truncateId(attempt.attemptedDeviceId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={attempt.existingDeviceId}
                    >
                      {truncateId(attempt.existingDeviceId)}
                    </Typography>
                  </TableCell>
                  <TableCell>{attempt.deviceModel || '-'}</TableCell>
                  <TableCell>
                    <Label variant="soft" color={SEVERITY_COLORS[getSeverity(attempt)]}>
                      {SEVERITY_LABELS_TR[getSeverity(attempt)]}
                    </Label>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(attempt.resolutionStatus)}
                      label={STATUS_LABELS[attempt.resolutionStatus]}
                      color={STATUS_COLORS[attempt.resolutionStatus]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{renderCounterpartyLink(attempt)}</TableCell>
                  <TableCell>
                    {attempt.resolutionStatus === SuspiciousLoginResolutionStatus.PENDING && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleReview(attempt)}
                      >
                        İncele
                      </Button>
                    )}
                    {attempt.resolutionStatus !== SuspiciousLoginResolutionStatus.PENDING &&
                      attempt.reviewedByName && (
                        <Typography variant="caption" color="text.secondary">
                          {attempt.reviewedByName}
                        </Typography>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog open={dialogOpen} onClose={() => !resolving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SecurityIcon color="warning" />
            <span>Şüpheli Giriş Denemesi İnceleme</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedAttempt && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Önem
                </Typography>
                <Label variant="soft" color={SEVERITY_COLORS[getSeverity(selectedAttempt)]}>
                  {SEVERITY_LABELS_TR[getSeverity(selectedAttempt)]}
                </Label>
              </Box>

              {getSeverity(selectedAttempt) === 'CRITICAL' && (
                <Alert severity="error">
                  Bu deneme başka bir çalışanın kayıtlı cihazıyla çakıştı — her iki hesabı da
                  inceleyin.
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Çalışan
                </Typography>
                <Typography>{selectedAttempt.employeeName}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  E-posta
                </Typography>
                <Typography>{selectedAttempt.employeeEmail}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Şube
                </Typography>
                <Typography>{selectedAttempt.branchName || '—'}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Denenen Cihaz ID
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selectedAttempt.attemptedDeviceId}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Mevcut Kayıtlı Cihaz ID
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selectedAttempt.existingDeviceId}
                </Typography>
              </Box>

              {selectedAttempt.deviceModel && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cihaz Modeli
                  </Typography>
                  <Typography>{selectedAttempt.deviceModel}</Typography>
                </Box>
              )}

              {selectedAttempt.osType && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    İşletim Sistemi
                  </Typography>
                  <Typography>{selectedAttempt.osType}</Typography>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Tarih
                </Typography>
                <Typography>
                  {format(new Date(selectedAttempt.createdAt), 'dd MMMM yyyy HH:mm:ss', { locale: tr })}
                </Typography>
              </Box>

              {selectedAttempt.counterpartyEmployeeId && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Çakışan Çalışan
                  </Typography>
                  {renderCounterpartyLink(selectedAttempt)}
                </Box>
              )}

              <TextField
                label="Notlar (İsteğe Bağlı)"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
              />

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={() => handleResolutionClick('APPROVED')}
                  disabled={resolving}
                  startIcon={<CheckCircleIcon />}
                >
                  Evet, Tanıyorum - Cihazı Sıfırla
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  onClick={() => handleResolutionClick('REJECTED')}
                  disabled={resolving}
                  startIcon={<CancelIcon />}
                >
                  Hayır, Tanımıyorum - Engelle
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={resolving}>
            İptal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Sub-Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => !resolving && setConfirmDialogOpen(false)}>
        <DialogTitle>
          {pendingResolution === 'APPROVED' ? 'Cihaz Sıfırlama Onayı' : 'Hesap Engelleme Onayı'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={pendingResolution === 'APPROVED' ? 'info' : 'error'} sx={{ mt: 1 }}>
            {pendingResolution === 'APPROVED'
              ? 'Çalışanın cihaz kaydı sıfırlanacak ve tekrar giriş yapabilecektir.'
              : 'Çalışan hesabı engellenecek ve giriş yapamayacaktır.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={resolving}>
            Vazgeç
          </Button>
          <Button
            onClick={handleConfirmResolve}
            variant="contained"
            color={pendingResolution === 'APPROVED' ? 'success' : 'error'}
            disabled={resolving}
          >
            {resolving ? 'İşleniyor...' : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
